import { Button } from "@/components/ui/button.tsx"
import { OrgFieldConfig, UpdateFieldConfigRequest } from "@/api/org_field_config.tsx"
import { OrgOption } from "@/api/org_options.tsx"
import { useState } from "preact/hooks"
import { Edit2, Save, X, Loader2 } from "lucide-react"
import { FieldConfigForm, FieldConfigFormData } from "./field_config_form.tsx"
import { api_fetch } from "@/api/client.tsx"
import { toast } from "@/components/ui/use-toast"

interface FieldConfigCardProps {
  config: OrgFieldConfig
  options: OrgOption[]
  onUpdate: (config: OrgFieldConfig) => void
  onDelete: (configId: string) => Promise<void>
  onUpdateOptions: (fieldName: string, options: OrgOption[], previousFieldName?: string) => Promise<void>
}

export function FieldConfigCard({ 
  config, 
  options, 
  onUpdate, 
  onDelete, 
  onUpdateOptions 
}: FieldConfigCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editingConfig, setEditingConfig] = useState<FieldConfigFormData>({
    field_name: config.field_name,
    field_type: config.field_type,
    display_name: config.display_name,
    display_order: config.display_order,
    is_active: config.is_active,
    table_contexts: config.table_contexts,
    tempOptions: options.map(opt => ({
      id: opt.id,
      value: opt.option_value,
      order: opt.display_order,
      matchPatterns: opt.match_patterns || [],
    }))
  })
  const [saving, setSaving] = useState(false)

  function startEdit() {
    setIsEditing(true)
    setEditingConfig({
      field_name: config.field_name,
      field_type: config.field_type,
      display_name: config.display_name,
      display_order: config.display_order,
      is_active: config.is_active,
      table_contexts: config.table_contexts,
      tempOptions: options.map(opt => ({
        id: opt.id,
        value: opt.option_value,
        order: opt.display_order,
        matchPatterns: opt.match_patterns || [],
      }))
    })
  }

  function cancelEdit() {
    setIsEditing(false)
    setEditingConfig({
      field_name: config.field_name,
      field_type: config.field_type,
      display_name: config.display_name,
      display_order: config.display_order,
      is_active: config.is_active,
      table_contexts: config.table_contexts,
      tempOptions: options.map(opt => ({
        id: opt.id,
        value: opt.option_value,
        order: opt.display_order,
        matchPatterns: opt.match_patterns || [],
      }))
    })
  }

  async function saveConfig() {
    setSaving(true)
    try {
      const updateRequest: UpdateFieldConfigRequest = {
        field_name: editingConfig.field_name,
        field_type: editingConfig.field_type,
        display_name: editingConfig.display_name,
        display_order: editingConfig.display_order,
        is_active: editingConfig.is_active,
        table_contexts: editingConfig.table_contexts
      }

      const result = await api_fetch<OrgFieldConfig>(`/org-field-configs/${config.id}`, {
        method: "PATCH",
        body: updateRequest
      })

      if (!result.ok) {
        throw new Error("Failed to update field config")
      }

      const savedConfig = result.value.data
      onUpdate(savedConfig)

      if (editingConfig.field_type === "dropdown" && editingConfig.tempOptions) {
        const newOptions = editingConfig.tempOptions.map((opt) => ({
          id: opt.id,
          org_id: config.org_id,
          field_name: savedConfig.field_name,
          option_value: opt.value,
          display_order: opt.order,
          is_active: true,
          match_patterns: opt.matchPatterns || [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }))
        await onUpdateOptions(
          savedConfig.field_name,
          newOptions,
          config.field_name !== savedConfig.field_name ? config.field_name : undefined
        )
      }

      setIsEditing(false)
      toast({
        title: "Success",
        description: "Field configuration updated",
      })
    } catch (error) {
      console.error("Failed to update field config:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update field configuration"
      })
    } finally {
      setSaving(false)
    }
  }

  async function deleteConfig() {
    if (!confirm("Are you sure you want to delete this field configuration?")) return

    setSaving(true)
    try {
      await onDelete(config.id)
    } catch (error) {
      console.error("Failed to delete field config:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete field configuration"
      })
    } finally {
      setSaving(false)
    }
  }

  if (isEditing) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Edit Field Configuration</h3>
          <div className="flex gap-2">
            <Button
              // @ts-ignore
              size="sm"
              onClick={saveConfig}
              disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save
            </Button>
            <Button
              // @ts-ignore
              size="sm"
              variant="outline"
              onClick={cancelEdit}
              disabled={saving}>
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>

        <FieldConfigForm
          config={editingConfig}
          onUpdate={setEditingConfig}
          showActiveField={true}
          showDisplayOrder={true}
        />

      </div>
    )
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium">{config.display_name}</h3>
          <p className="text-sm text-gray-600">
            {config.field_name} • {config.field_type}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            // @ts-ignore
            size="sm"
            variant="outline"
            onClick={startEdit}>
            <Edit2 className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <Button
            // @ts-ignore
            size="sm"
            variant="outline"
            onClick={deleteConfig}
            disabled={saving}
            className="text-red-600 hover:text-red-700">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <X className="w-4 h-4 mr-1" />
            )}
            Delete
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-sm">
          <span className="font-medium">Table Contexts:</span>{" "}
          {config.table_contexts.join(", ")}
        </div>
        <div className="text-sm">
          <span className="font-medium">Display Order:</span> {config.display_order}
        </div>
        <div className="text-sm">
          <span className="font-medium">Status:</span>{" "}
          <span className={config.is_active ? "text-green-600" : "text-red-600"}>
            {config.is_active ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {config.field_type === "dropdown" && options.length > 0 && (
        <div className="mt-4">
          <h4 className="font-medium mb-2">Dropdown Options:</h4>
          <div className="space-y-1">
            {options
              .sort((a, b) => a.display_order - b.display_order)
              .map((option, index) => (
                <div key={option.id} className="text-sm text-gray-600">
                  <div>
                    {index + 1}. {option.option_value}
                  </div>
                  {option.match_patterns && option.match_patterns.length > 0 ? (
                    <div className="pl-5 text-xs text-gray-500">
                      Patterns: {option.match_patterns.join(", ")}
                    </div>
                  ) : null}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
